import type { MediaUploadUsageValue } from '@/lib/enums/MediaUploadUsage'
import { cn } from '@/lib/utils'
import { canEditMedia } from '@/lib/media-editing'
import { DEFAULT_UPLOAD_USAGE } from '@/lib/media-upload-profiles'
import type { MediaFile } from '@/lib/services/media/media.types'
import { type CSSProperties, useState } from 'react'
import type { LightboxTriggerOptions, LightboxTriggerProps } from './ImageLightboxGallery'
import ImageLightboxPreviewTrigger from './ImageLightboxPreviewTrigger'
import ImageTileControls from './ImageTileControls'
import ImageTileMetadata, { type ImageTileMetadataVisibility } from './ImageTileMetadata'
import LazyImageEditorDialog from './LazyImageEditorDialog'
import MediaFocalPointDialog from './MediaFocalPointDialog'

interface UploadedMediaPreviewTileProps {
  media: MediaFile
  alt: string
  caption?: string
  onRemove?: (() => void) | null
  /** Receives the saved edit; editing is offered only when this is set. */
  onEditComplete?: ((editedMedia: MediaFile) => void) | null
  allowFocalPoint?: boolean
  onFocalPointChange?: ((media: MediaFile) => void) | null
  /** Opens the caller's own framing editor in place of the tile's. */
  onAdjustFraming?: (() => void) | null
  /** Saves an edited photo in place of uploading it, and answers with the saved file. */
  saveEditedFile?: ((edit: { file: File; media: MediaFile }) => Promise<MediaFile>) | null
  removeLabel?: string
  editLabel?: string
  editUploadUsage?: MediaUploadUsageValue
  editAttachToPhotoManager?: boolean
  showMetadata?: boolean
  metadataVisibility?: ImageTileMetadataVisibility
  metadataTitle?: string
  metadataDescription?: string
  wrapperClassName?: string
  frameClassName?: string
  frameStyle?: CSSProperties
  imageClassName?: string
  imageStyle?: CSSProperties
  triggerClassName?: string
  /** Attributes for the preview's own trigger, such as a drag handle's. */
  triggerProps?: Omit<LightboxTriggerOptions, 'mode'>
  /** A trigger from a gallery around the tile; the preview then opens that gallery. */
  lightboxTriggerProps?: LightboxTriggerProps
}

const UploadedMediaPreviewTile = ({
  media,
  alt,
  caption,
  onRemove = null,
  onEditComplete = null,
  allowFocalPoint = false,
  onFocalPointChange = null,
  onAdjustFraming = null,
  saveEditedFile = null,
  removeLabel = 'Remove image',
  editLabel = 'Edit image',
  editUploadUsage = DEFAULT_UPLOAD_USAGE,
  editAttachToPhotoManager = false,
  showMetadata = false,
  metadataVisibility = 'hover',
  metadataTitle = undefined,
  metadataDescription = undefined,
  wrapperClassName = '',
  frameClassName = '',
  frameStyle = undefined,
  imageClassName = '',
  imageStyle = undefined,
  triggerClassName = '',
  triggerProps = undefined,
  lightboxTriggerProps = undefined,
}: UploadedMediaPreviewTileProps) => {
  const [isEditorOpen, setIsEditorOpen] = useState(false)
  const [isFocalPointOpen, setIsFocalPointOpen] = useState(false)

  if (!media?.url) {
    return null
  }

  const canEdit = Boolean(onEditComplete) && canEditMedia(media)
  const canAdjustFraming = allowFocalPoint && (Boolean(onAdjustFraming) || Boolean(media?.id))
  const tileTitle = metadataTitle ?? caption ?? media.name

  return (
    <div className={cn('group relative isolate', wrapperClassName)}>
      <ImageLightboxPreviewTrigger
        media={media}
        alt={alt}
        caption={caption}
        triggerMode="button"
        elementProps={lightboxTriggerProps ? undefined : triggerProps}
        triggerProps={lightboxTriggerProps}
        frameClassName={frameClassName}
        frameStyle={frameStyle}
        imageClassName={imageClassName}
        imageStyle={imageStyle}
        triggerClassName={triggerClassName}
      />
      <ImageTileControls
        canEdit={canEdit}
        canAdjustFraming={canAdjustFraming}
        canRemove={Boolean(onRemove)}
        editLabel={editLabel}
        removeLabel={removeLabel}
        onEdit={() => setIsEditorOpen(true)}
        onAdjustFraming={onAdjustFraming ?? (() => setIsFocalPointOpen(true))}
        onRemove={onRemove}
      />
      {canEdit ? (
        <LazyImageEditorDialog
          open={isEditorOpen}
          onOpenChange={setIsEditorOpen}
          media={media}
          uploadUsage={editUploadUsage}
          attachToPhotoManager={editAttachToPhotoManager}
          saveEditedFile={saveEditedFile}
          onEditComplete={onEditComplete}
        />
      ) : null}
      {allowFocalPoint && media?.id && !onAdjustFraming ? (
        <MediaFocalPointDialog
          open={isFocalPointOpen}
          onOpenChange={setIsFocalPointOpen}
          media={media}
          onApply={onFocalPointChange}
        />
      ) : null}
      {showMetadata ? (
        <ImageTileMetadata
          media={media}
          title={tileTitle}
          description={metadataDescription}
          visibility={metadataVisibility}
        />
      ) : null}
    </div>
  )
}

export default UploadedMediaPreviewTile
